import { useState } from 'react';
import BackendLayout from '../../components/feature/BackendLayout';
import { mockAffiliateLinks, mockProducts } from '../../mocks/products';

export default function AffiliateLinksPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [previewLinkId, setPreviewLinkId] = useState<string | null>(null);
  const [editLinkId, setEditLinkId] = useState<string | null>(null);

  const editLink = editLinkId ? mockAffiliateLinks.find((l) => l.id === editLinkId) : null;
  const editProduct = editLink ? mockProducts.find((p) => p.name === editLink.productName) : null;

  const [editForm, setEditForm] = useState({
    productName: '',
    fullUrl: '',
    shortUrl: '',
    status: 'active' as 'active' | 'paused',
  });
  const [editSaved, setEditSaved] = useState(false);

  const openEditModal = (linkId: string) => {
    const link = mockAffiliateLinks.find((l) => l.id === linkId);
    if (link) {
      setEditForm({
        productName: link.productName,
        fullUrl: link.fullUrl,
        shortUrl: link.shortUrl,
        status: link.status as 'active' | 'paused',
      });
      setEditSaved(false);
      setEditLinkId(linkId);
    }
  };

  const filteredLinks = mockAffiliateLinks.filter((link) => {
    const matchesSearch = link.productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || link.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const previewLink = previewLinkId ? mockAffiliateLinks.find((l) => l.id === previewLinkId) : null;
  const previewProduct = previewLink
    ? mockProducts.find((p) => p.name === previewLink.productName)
    : null;

  return (
    <BackendLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Affiliate Links</h1>
          <p className="text-sm text-gray-500">Manage and track all your affiliate links</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-5 py-2.5 bg-[#0A0A0A] text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line"></i> Create New Link
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-links-line text-xl text-teal-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">{mockAffiliateLinks.length}</div>
              <div className="text-xs text-gray-500">Total Links</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-xl text-emerald-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">
                {mockAffiliateLinks.filter((l) => l.status === 'active').length}
              </div>
              <div className="text-xs text-gray-500">Active</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-cursor-line text-xl text-sky-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">
                {mockAffiliateLinks.reduce((sum, l) => sum + l.clicks, 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total Clicks</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-xl text-amber-600"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">
                ${mockAffiliateLinks.reduce((sum, l) => sum + l.revenue, 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total Revenue</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search links..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {['all', 'active', 'paused'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  statusFilter === status
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Links Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Short URL</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Clicks</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Conversions</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredLinks.map((link) => (
                <tr key={link.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-medium text-gray-900">{link.productName}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-teal-600 font-mono">{link.shortUrl}</span>
                      <button className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-900 font-medium">{link.clicks.toLocaleString()}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-900 font-medium">{link.conversions}</td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-semibold text-emerald-600">${link.revenue.toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      link.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {link.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{link.createdAt}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPreviewLinkId(link.id)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-all cursor-pointer"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => openEditModal(link.id)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Slide-over Panel */}
      {previewLinkId && (
        <div className="fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setPreviewLinkId(null)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
            {/* Panel Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-gray-900">Link Details</h3>
              <button
                onClick={() => setPreviewLinkId(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {previewLink && (
              <div className="p-6 space-y-6">
                {/* Product Image */}
                {previewProduct && (
                  <div className="w-full h-48 rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={previewProduct.image}
                      alt={previewProduct.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}

                {/* Product Name & Status */}
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-xl font-bold text-gray-900 leading-tight">{previewLink.productName}</h4>
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${
                      previewLink.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {previewLink.status}
                    </span>
                  </div>
                  {previewProduct && (
                    <p className="text-sm text-gray-500 leading-relaxed">{previewProduct.description}</p>
                  )}
                </div>

                {/* Product Info */}
                {previewProduct && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Pricing</div>
                      <div className="text-sm font-bold text-gray-900">{previewProduct.pricing}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Commission</div>
                      <div className="text-sm font-bold text-teal-600">{previewProduct.commission}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Rating</div>
                      <div className="text-sm font-bold text-amber-600 flex items-center justify-center gap-1">
                        <i className="ri-star-fill text-xs"></i>
                        {previewProduct.rating}
                      </div>
                    </div>
                  </div>
                )}

                {/* Link Performance */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Performance</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-sky-50/60 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-cursor-line text-xs text-sky-600"></i>
                        </div>
                        <span className="text-xs text-gray-500">Clicks</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">{previewLink.clicks.toLocaleString()}</div>
                    </div>
                    <div className="bg-emerald-50/60 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-exchange-line text-xs text-emerald-600"></i>
                        </div>
                        <span className="text-xs text-gray-500">Conversions</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">{previewLink.conversions}</div>
                    </div>
                    <div className="bg-amber-50/60 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-money-dollar-circle-line text-xs text-amber-600"></i>
                        </div>
                        <span className="text-xs text-gray-500">Revenue</span>
                      </div>
                      <div className="text-lg font-bold text-emerald-600">${previewLink.revenue.toLocaleString()}</div>
                    </div>
                    <div className="bg-violet-50/60 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-percent-line text-xs text-violet-600"></i>
                        </div>
                        <span className="text-xs text-gray-500">Conv. Rate</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {((previewLink.conversions / previewLink.clicks) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Link URLs */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Link URLs</h5>
                  <div className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Short URL</div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-teal-600 font-mono truncate flex-1">{previewLink.shortUrl}</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(previewLink.shortUrl)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-all cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-file-copy-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Full URL</div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 font-mono truncate flex-1">{previewLink.fullUrl}</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(previewLink.fullUrl)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-all cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-file-copy-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {previewProduct && previewProduct.tags.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900 mb-3">Tags</h5>
                    <div className="flex flex-wrap gap-2">
                      {previewProduct.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {previewProduct && previewProduct.features.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900 mb-3">Key Features</h5>
                    <ul className="space-y-2">
                      {previewProduct.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-600">
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-xs text-teal-500"></i>
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Dates */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-900 font-medium">{previewLink.createdAt}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-gray-500">Last Click</span>
                    <span className="text-gray-900 font-medium">{previewLink.lastClick}</span>
                  </div>
                </div>

                {/* Action Button */}
                <a
                  href={previewLink.fullUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="block w-full text-center px-5 py-3 bg-[#0A0A0A] text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line mr-2"></i>Visit Affiliate Link
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-gray-900">Create New Affiliate Link</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Name</label>
                <input type="text" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white" placeholder="Enter product name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Destination URL</label>
                <input type="url" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white" placeholder="https://example.com/product" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Custom Slug (optional)</label>
                <input type="text" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white" placeholder="my-custom-slug" />
              </div>
              <div className="flex gap-3 pt-3">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-5 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-5 py-2.5 bg-[#0A0A0A] text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  Create Link
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editLinkId && editLink && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-gray-900">Edit Affiliate Link</h3>
              <button
                onClick={() => setEditLinkId(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {editProduct && (
              <div className="flex items-center gap-3 mb-5 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src={editProduct.image} alt={editProduct.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{editProduct.name}</div>
                  <div className="text-xs text-gray-500">{editProduct.category} &middot; {editProduct.commission} commission</div>
                </div>
              </div>
            )}

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setEditSaved(true);
                setTimeout(() => {
                  setEditLinkId(null);
                }, 1200);
              }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Name</label>
                <input
                  type="text"
                  value={editForm.productName}
                  onChange={(e) => setEditForm({ ...editForm, productName: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white"
                  placeholder="Enter product name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Destination URL</label>
                <input
                  type="url"
                  value={editForm.fullUrl}
                  onChange={(e) => setEditForm({ ...editForm, fullUrl: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white"
                  placeholder="https://example.com/product"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Short URL</label>
                <input
                  type="text"
                  value={editForm.shortUrl}
                  onChange={(e) => setEditForm({ ...editForm, shortUrl: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm bg-white"
                  placeholder="https://aff.link/my-slug"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, status: 'active' })}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      editForm.status === 'active'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-check-line mr-1.5"></i>Active
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, status: 'paused' })}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      editForm.status === 'paused'
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-pause-line mr-1.5"></i>Paused
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setEditLinkId(null)}
                  className="flex-1 px-5 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-5 py-2.5 bg-[#0A0A0A] text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
                >
                  {editSaved ? (
                    <>
                      <i className="ri-check-line"></i> Saved
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </BackendLayout>
  );
}
